import React, { type ComponentPropsWithRef, type ComponentType } from "react";
import { useDispatch, useSelector } from "react-redux";
import { type RouteObject, useNavigate } from "react-router-dom-v5-compat";
import type { AppDispatch, TripProduct } from "@b2bportal/core-types";
import { mapQueryResponse } from "@b2bportal/core-utilities";
import {
  HotelItineraryState,
  type HotelItinerarySummary,
} from "@b2bportal/lodging-api";
import { Product } from "@b2bportal/purchase-api";
import {
  HotelReservationCard,
  HotelReservationPageRoute,
} from "@tenant/hotels";
import { hotelsPostBookingApi } from "../store";

export type HotelTripProduct = TripProduct<HotelItinerarySummary>;

export const useHotelTripProduct = ({
  tripsPath,
  reservationPath,
}: {
  tripsPath: string;
  reservationPath: (reservationId: string) => string;
}): {
  hotels: { [Product.Hotel]: HotelTripProduct };
  pastHotels: { [Product.Hotel]: HotelTripProduct };
  hotelReservationRouteProps: RouteObject;
} => {
  const dispatch = useDispatch<AppDispatch>();
  const navigate = useNavigate();

  const product = React.useMemo(
    () => ({
      fetch: () => {
        dispatch(
          hotelsPostBookingApi.endpoints.fetchAllHotelReservations.initiate()
        );
      },
      component: ({
        reservation,
        ...props
      }: ComponentPropsWithRef<
        ComponentType<{ reservation: HotelItinerarySummary }>
      >) => (
        <HotelReservationCard
          reservationSummary={reservation}
          selectItinerary={() => {
            navigate(reservationPath(reservation.reservationBookingId));
          }}
          reservation={reservation}
          {...props}
        />
      ),
    }),
    [dispatch, navigate, reservationPath]
  );

  const reservations = useSelector(
    hotelsPostBookingApi.endpoints.fetchAllHotelReservations.select()
  );

  return React.useMemo(
    () => ({
      hotels: {
        [Product.Hotel]: {
          ...product,
          reservations: mapQueryResponse(reservations, (data) => [
            ...(data.itineraries[HotelItineraryState.Future] ?? []),
            ...(data.itineraries[HotelItineraryState.Present] ?? []),
          ]),
        },
      },
      pastHotels: {
        [Product.Hotel]: {
          ...product,
          reservations: mapQueryResponse(reservations, (data) => [
            ...(data.itineraries[HotelItineraryState.Past] ?? []),
            ...(data.itineraries[HotelItineraryState.Canceled] ?? []),
          ]),
        },
      },
      hotelReservationRouteProps: {
        path: reservationPath(":reservationId"),
        element: <HotelReservationPageRoute />,
      },
    }),
    [product, reservationPath, reservations]
  );
};
